import { ButtonGradientPrimary, ButtonSecondary, ModalDialog } from '@amelio/ui-library';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useUpdateThematicMeetingSchedule } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useSaveThematicMeeting } from '@/pages/ThematicMeetings/hooks/useSaveThematicMeeting';
import {
    SCHEDULED_AT_PANEL_SELECTOR,
    ScheduledAtField,
} from '@/pages/ThematicMeetings/Plan/components/ScheduledAtField';
import { showSuccessToast } from '@/plugins/Toast';

/** Whether the date field's panel is open. Portalled into the module's portal target, it shares the
 *  field's root node: the host's shadow root, or the document when the module runs on its own. */
const isDatePanelOpen = (field: HTMLElement | null) => {
    const root = field?.getRootNode() as Document | ShadowRoot | undefined;
    return Boolean(root?.querySelector(SCHEDULED_AT_PANEL_SELECTOR));
};

/**
 * Lets Escape close the open date panel alone. The library's modal and its date panel both listen on
 * `document`, and the modal, registered first, would close the whole window and drop the change. A
 * capture listener on `window` runs before both: it notes that this Escape belongs to the panel, and
 * the modal's close then lets it through.
 */
const useEscapeClosesDatePanelFirst = () => {
    const fieldRef = useRef<HTMLDivElement>(null);
    const isEscapeForPanelRef = useRef(false);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || !isDatePanelOpen(fieldRef.current)) {
                return;
            }
            isEscapeForPanelRef.current = true;
            // Never outlives this key press, whoever else handles it.
            setTimeout(() => {
                isEscapeForPanelRef.current = false;
            });
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, []);

    const consumeEscapeForPanel = () => {
        const isForPanel = isEscapeForPanelRef.current;
        isEscapeForPanelRef.current = false;
        return isForPanel;
    };

    return { fieldRef, consumeEscapeForPanel };
};

interface MeetingDateModalProps {
    meeting: ThematicMeeting;
    onClose: () => void;
}

/**
 * Moves a planned meeting to another date, or leaves it without one, with the date field of the
 * creation flow. Opened from the status pill, which shows that date. Mounted only while open, so it
 * always starts from the meeting's current date.
 */
export const MeetingDateModal = ({ meeting, onClose }: MeetingDateModalProps) => {
    const { t } = useAmelioTranslations();
    const { save } = useSaveThematicMeeting();
    const { mutateAsync: updateSchedule, isPending: isSaving } = useUpdateThematicMeetingSchedule();
    const [scheduledAt, setScheduledAt] = useState(() =>
        meeting.scheduledAt ? new Date(meeting.scheduledAt) : undefined,
    );
    const { fieldRef, consumeEscapeForPanel } = useEscapeClosesDatePanelFirst();

    const handleDialogClose = () => {
        if (!consumeEscapeForPanel()) {
            onClose();
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const isSaved = await save(() =>
            updateSchedule({
                id: meeting.id,
                request: {
                    scheduledAt: scheduledAt?.toISOString() ?? null,
                    durationMinutes: meeting.durationMinutes,
                },
            }),
        );
        if (isSaved) {
            showSuccessToast({ message: t('thematicMeetings.editMeeting.updateSuccess') });
            onClose();
        }
    };

    return (
        <ModalDialog
            isOpen
            onClose={handleDialogClose}
            closeAriaLabel={t('common.close')}
            title={t('thematicMeetings.editMeeting.dateTitle')}
            size={'md'}
            radius={'4xl'}
            padding={'xl'}
            shadow={'2xl'}
            bordered
            backdropBlur
            onSubmit={(event) => void handleSubmit(event)}
            dataTestId={'meeting-date-modal'}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        type={'button'}
                        onClick={onClose}
                        size={'md'}
                    >
                        {t('thematicMeetings.editMeeting.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        disabled={isSaving}
                        loading={isSaving}
                        size={'md'}
                    >
                        {isSaving
                            ? t('thematicMeetings.editMeeting.saving')
                            : t('thematicMeetings.editMeeting.save')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div ref={fieldRef}>
                <ScheduledAtField
                    scheduledAt={scheduledAt}
                    onDateChange={setScheduledAt}
                />
            </div>
        </ModalDialog>
    );
};
